<template>
    <div>
        <div class="md:flex md:items-center md:justify-between">
            <div class="min-w-0 flex-1">
                <router-link
                    :to="{ name: 'editVenue' }"
                    class="mb-2 flex items-center text-sm text-gray-500"
                >
                    <base-back-icon />
                    <span>Settings</span>
                </router-link>
                <base-page-title title="Zonal Settings" />
            </div>

            <BaseSpinner :isLoading="isLoading" />
        </div>

        <div class="mt-4 md:grid md:grid-cols-3 md:gap-6">
            <div class="md:col-span-1">
                <div class="sm:pl-0 sm:pr-5">
                    <h3 class="text-base font-semibold leading-6 text-gray-800">
                        Details
                    </h3>
                    <p class="mt-2 text-sm text-gray-500">
                        Connect to Zonal EPOS. When enabled, your orders will be
                        automatically synced to Zonal.
                    </p>
                </div>
            </div>
            <div
                v-if="!isLoading"
                class="mt-5 w-full bg-white p-2 text-left shadow sm:rounded-md sm:p-4 md:col-span-2 md:mt-0"
            >
                <div class="space-y-6">
                    <div v-if="!isAccountConnected">
                        <base-password-input
                            id="zonal-brand-token"
                            label="iOrder Brand Token"
                            v-model="connectForm.brandToken"
                            class="mb-4"
                        />
                        <base-input-field
                            id="zonal-user-device-identifier"
                            label="iOrder User Device Identifier"
                            v-model="connectForm.userDeviceIdentifier"
                            class="mb-4"
                        />
                        <base-input-field
                            id="zonal-bundle-identifier"
                            label="Bundle Identifier"
                            v-model="connectForm.bundleIdentifier"
                            class="mb-4"
                        />
                        <div class="flex justify-end">
                            <base-button
                                button-text="Connect to Zonal"
                                :isLoading="isLoading"
                                :disabled="isLoading"
                                @clicked="connectAccount"
                            />
                        </div>
                    </div>

                    <div v-else>
                        <toggle-switch
                            label="Enable Zonal"
                            v-model="settings.enabled"
                            class="mb-4"
                            @toggle-enable="toggleSettings"
                        />

                        <div v-if="settings.enabled">
                            <base-select-dropdown
                                id="zonalVenue"
                                v-model="settings.zonalVenueId"
                                label="Zonal Venue"
                                :options="zonalVenues"
                                valueKey="id"
                                labelKey="name"
                                emitValueOnly
                                class="mb-4 block w-full min-w-0 flex-1 rounded-none rounded-r-md border-gray-300 focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm"
                            />

                            <base-input-field
                                id="zonal-order-note-product-id"
                                label="Order Note Product ID"
                                v-model="settings.orderNoteProductId"
                                class="mb-4"
                            />

                            <base-input-field
                                id="zonal-discount-currency-id"
                                label="Discount Currency ID"
                                v-model="settings.discountCurrencyId"
                                class="mb-4"
                            />

                            <base-input-field
                                id="zonal-discount-percentage-id"
                                label="Discount Percentage ID"
                                v-model="settings.discountPercentageId"
                                class="mb-4"
                            />

                            <div class="sm:col-span-6">
                                <label
                                    for="paymentMethod"
                                    class="flex items-center text-sm font-medium leading-5 text-gray-700"
                                >
                                    Payment Method ID
                                </label>
                                <base-select-dropdown
                                    id="paymentMethod"
                                    v-model="settings.paymentMethodId"
                                    :options="zonalPaymentMethods"
                                    valueKey="paymentMethodId"
                                    labelKey="paymentMethodName"
                                    emitValueOnly
                                    class="-mt-2 mb-4 block w-full min-w-0 flex-1 rounded-none rounded-r-md border-gray-300 focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm"
                                />
                            </div>

                            <div class="sm:col-span-6">
                                <label
                                    for="enableCoursing"
                                    class="mb-1 flex items-center text-sm font-medium leading-5 text-gray-700"
                                >
                                    Enable Coursing
                                </label>
                                <toggle-switch
                                    id="enableCoursing"
                                    class="mb-4"
                                    v-model.trim="settings.enableCoursing"
                                    @input="saveVenueConfig"
                                />
                            </div>

                            <div class="sm:col-span-6">
                                <label
                                    for="enableCategoryMerge"
                                    class="mb-1 flex items-center text-sm font-medium leading-5 text-gray-700"
                                >
                                    Enable Category Merge
                                </label>
                                <toggle-switch
                                    id="enableCategoryMerge"
                                    class="mb-4"
                                    v-model="settings.enableCategoryMerge"
                                    @input="saveVenueConfig"
                                />
                            </div>

                            <div class="sm:col-span-6">
                                <order-note-template
                                    v-model="settings.orderNoteTemplate"
                                    :max-length="250"
                                    tooltip="Customize the order notes sent to Zonal. Variables will be replaced with actual order data."
                                    placeholder="Enter your order note template..."
                                />
                            </div>

                            <div class="flex border-t border-gray-200 pt-4">
                                <base-button
                                    button-text="Sync tables"
                                    :isLoading="isSyncing"
                                    :disabled="isSyncing"
                                    @clicked="syncTables"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <BaseSpacerWithRuler />

        <div v-if="settings.enabled" class="flex justify-end">
            <span
                class="inline-flex rounded-md shadow-sm"
                @click="saveVenueConfig"
            >
                <button
                    type="button"
                    class="relative inline-flex items-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium leading-5 text-white hover:bg-indigo-500 focus:border-indigo-700 focus:shadow-outline-indigo focus:outline-none active:bg-indigo-700"
                    :disabled="isLoading"
                >
                    Save
                </button>
            </span>
        </div>
    </div>
</template>

<script>
import OrderNoteTemplate from './OrderNoteTemplate.vue';
import ToggleSwitch from '@/components/formComponents/ToggleSwitch';

export default {
    name: 'ZonalSettings',
    metaInfo: {
        title: 'Zonal Integration'
    },
    components: {
        ToggleSwitch,
        OrderNoteTemplate
    },
    props: {
        venueId: {
            type: Number,
            required: true
        },
        venue: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            isLoading: false,
            isSyncing: false,
            accountConfig: { enabled: false },
            connectForm: {
                brandToken: '',
                userDeviceIdentifier: '',
                bundleIdentifier: ''
            },
            settings: { enabled: false },
            zonalPaymentMethods: [],
            zonalVenues: []
        };
    },
    computed: {
        isAccountConnected() {
            return this.accountConfig.enabled;
        }
    },
    async created() {
        try {
            this.isLoading = true;
            await this.fetchAccountConfig();
            if (this.isAccountConnected) {
                await Promise.all([
                    this.fetchZonalVenues(),
                    this.fetchVenueConfig(),
                    this.fetchZonalPaymentMethods()
                ]);
            }
        } finally {
            this.isLoading = false;
        }
    },
    methods: {
        async toggleSettings() {
            try {
                await this.$nextTick();
                await this.saveVenueConfig();
            } catch (error) {
                throw new Error(`API ${error}`);
            }
        },
        async fetchAccountConfig() {
            try {
                const { data } = await this.$axios.get('/zonal/config');

                if (data && data.success && data.config) {
                    this.accountConfig = { enabled: !!data.config.enabled };
                } else {
                    this.accountConfig = { enabled: false };
                }
            } catch (error) {
                this.accountConfig = { enabled: false };

                throw new Error(`API ${error}`);
            }
        },
        async connectAccount() {
            try {
                this.isLoading = true;

                const payload = {
                    venueId: this.venueId,
                    brandToken: this.connectForm.brandToken,
                    userDeviceIdentifier: this.connectForm.userDeviceIdentifier,
                    iorderBundleIdentifier: this.connectForm.bundleIdentifier
                };

                await this.$axios.post('/zonal/connect', payload);
                await this.fetchAccountConfig();
                await this.fetchZonalVenues();
                await this.fetchVenueConfig();
                await this.fetchZonalPaymentMethods();

                this.$notify({
                    group: 'settings',
                    title: 'Connected to Zonal'
                });
            } catch (error) {
                throw new Error(`API ${error}`);
            } finally {
                this.isLoading = false;
            }
        },
        async fetchVenueConfig() {
            try {
                const { data } = await this.$axios.get(
                    `/venues/${this.venueId}/integrations/zonal/config`
                );

                if (data && data.success && data.config) {
                    this.settings = {
                        enabled: !!data.config.enabled,
                        orderNoteProductId:
                            data.config.orderNoteProductId || '',
                        orderNoteTemplate: data.config.orderNoteTemplate || '',
                        paymentMethodId: data.config.paymentMethodId || null,
                        discountCurrencyId:
                            data.config.discountCurrencyId || '',
                        discountPercentageId:
                            data.config.discountPercentageId || '',
                        enableCoursing: !!data.config.enableCoursing,
                        enableCategoryMerge: !!data.config.enableCategoryMerge,
                        zonalVenueId: Number(this.venue.posId)
                    };
                } else {
                    this.settings = { enabled: false };
                }
            } catch (error) {
                this.settings = { enabled: false };

                throw new Error(`API ${error}`);
            }
        },
        async saveVenueConfig() {
            this.isLoading = true;

            try {
                const payload = {
                    enabled: this.settings.enabled,
                    orderNoteProductId: this.settings.orderNoteProductId,
                    orderNoteTemplate: this.settings.orderNoteTemplate,
                    paymentMethodId: this.settings.paymentMethodId,
                    discountCurrencyId: this.settings.discountCurrencyId,
                    discountPercentageId: this.settings.discountPercentageId,
                    enableCoursing: this.settings.enableCoursing,
                    enableCategoryMerge: this.settings.enableCategoryMerge,
                    zonalVenueId: Number(this.settings.zonalVenueId) || null
                };

                await this.$axios.post(
                    `/venues/${this.venueId}/zonal/config`,
                    payload
                );

                this.$notify({ group: 'settings', title: 'Settings saved' });
            } catch (error) {
                throw new Error(`API ${error}`);
            } finally {
                this.isLoading = false;
            }
        },
        async fetchZonalVenues() {
            try {
                const { data } = await this.$axios.get('/zonal/venues');

                this.zonalVenues = Array.isArray(data) ? data : [];
            } catch (error) {
                this.zonalVenues = [];

                throw new Error(`API ${error}`);
            }
        },
        async syncTables() {
            try {
                this.isSyncing = true;

                await this.$axios.post('/zonal/tables', {
                    venueId: this.venueId
                });

                this.$notify({
                    group: 'settings',
                    title: 'Table sync started',
                    text: 'Table synchronization is running in the background',
                    type: 'success'
                });
            } catch (error) {
                this.$notify({
                    group: 'settings',
                    title: 'Table sync failed',
                    text: 'Please check your configuration and try again',
                    type: 'error'
                });

                console.error('Zonal table sync error:', error);
            } finally {
                this.isSyncing = false;
            }
        },
        async fetchZonalPaymentMethods() {
            try {
                const { data } = await this.$axios.get(
                    '/zonal/payment-methods',
                    {
                        params: {
                            venueId: this.venueId
                        }
                    }
                );

                this.zonalPaymentMethods = Array.isArray(data.paymentMethods)
                    ? data.paymentMethods
                    : [];

                if (
                    !this.settings.paymentMethodId &&
                    data.estate_payment_method_id
                ) {
                    this.settings.paymentMethodId =
                        data.estate_payment_method_id;
                }
            } catch (error) {
                throw new Error(`API ${error}`);
            }
        }
    }
};
</script>
